/// <reference path="../../typings/browser.d.ts" />

namespace Application.Models {
    'use strict';

    export interface ISonNode extends ITopologyElement {
        id?: number;
        name?: string;
        group?: string;
        multiservice?: boolean;
        active?: boolean;
        index?: number; // used for vis map drawing
        x?: number;
        y?: number;
    }

    export interface ISonLink extends ITopologyElement {
        id?: number;
        name?: string;
        capacity?: number;
        source?: SonNode;
        sourceHref: string;
        targetHref: string;
        target?: SonNode;
        weight?: number;
        src_port_label?: string;
        dst_port_label?: string;
        stats?: {
            load?: number;
            bitrate?: number;
        };
        statsToPlot?: ISonLinkStatsToPlot;
        active?: boolean;
        multiservice?: boolean;
        bw?: number;
        vlink?: boolean;
        fromTo?: string;
        index?: number;
        total?: number;
        visited: boolean;
    }

    export interface ISonLinkStatsToPlot {
        load?: number[];
        bitrate?: number[];
    }

    export interface ISonPacketmap {
        switches: ISonNode[];
        links: ISonLink[];
    }

    export interface ISonHealth {
        net: string;
        live_srv: number;
        future_srv: number;
        uptime: number;
    }

    export interface IAllSonElements {
        sources: ISimpleSonElement[];
        destinations: ISimpleSonElement[];
    }

    export interface ISimpleSonElement {
        uid: string;
        group?: "sources" | "destinations" | "connections" | "nodes";
        meta?: ISONElementMeta;
    }

    export interface ISONElementMeta {
        isActive: boolean;
        xpoint: number;
        priority: number;
        igmp: boolean;
        fqdn: string;
        dns: string;
        limiter: number;
        label: string;
        bw: number;
        node_id: number;
    }

    export class SimpleSonElement implements  ISimpleSonElement {

        public static Empty(group: "sources" | "destinations" | "connections" | "nodes"): SimpleSonElement {
            return new SimpleSonElement(null, group, {
                isActive: true,
                xpoint: -1,
                priority: -1,
                igmp: false,
                fqdn: null,
                dns: "",
                limiter: -1,
                label: "",
                bw: -1,
                node_id: -1
            });
        }

        constructor(public uid: string,
            public group: "sources" | "destinations" | "connections" | "nodes",
            public meta: ISONElementMeta) {

        }
    }

    export class AllSonElements implements  IAllSonElements {
        public destinations: ISimpleSonElement[];
        public sources: ISimpleSonElement[];

        constructor() {
            this.sources = [];
            this.destinations = [];
        }

        public static Empty(): AllSonElements {
            return new AllSonElements();
        }
    }

    export interface ISimpleSonDataPort extends ISimpleSonElement {
        hostname: string;
    }

    export class SonPacketmap implements ISonPacketmap {
        public switches: ISonNode[];
        public links: ISonLink[];

        public static burnish(packet: ISonPacketmap): void {
            if (packet !== undefined) {
                if (packet.switches !== undefined) {
                    packet.switches = packet.switches.map((n: ISonNode) => {
                        return new SonNode(n);
                    });
                }
                if (packet.links !== undefined) {
                    packet.links = packet.links.map((l: ISonLink) => {
                        return new SonLink(l);
                    });
                }
            }
        }

        constructor() {
            this.switches = [];
            this.links = [];
        }

        public static Empty(): SonPacketmap {
            return new SonPacketmap();
        }
    }

    export class SonNode implements ISonNode {
        public static switchesClass: string = 'switches';
        public static switchesPreHref: string = `/${SonNode.switchesClass}/`;

        public href: string;
        public id: number;
        public name: string;
        public group: string;
        public multiservice: boolean;
        public active: boolean;
        public x: number;
        public y: number;

        constructor(n: ISonNode) {
            this.href = SonNode.switchesPreHref + n.id;
            this.id = n.id;
            this.name = n.name;
            this.group = n.group;
            this.multiservice = n.multiservice;
            this.active = n.active;
            this.x = n.x;
            this.y = n.y;
        }

        public class(): TopologyElementType {
            return TopologyElementType.Switch;
        }

        public getDisplayLabel(): string {
            return this.name;
        }

        public getDisplayImage(): string {
            let status: string = undefined;
            if (this.active) {
                status = "ONLINE";
            } else {
                status = "OFFLINE";
            }
            let typeId: number = this.getType(); // at this time there's only one type of switch
            return `images/switchTypes/${typeId}-${status}.png`;
        }

        private getType(): number {
            switch (this.group) {
                case 'vport':
                    return 2;
                default:
                    return 1;
            }
        }
    }

    export class SonLink implements ISonLink {
        public static sonLinkClass: string = 'sonLinks';
        public static sonLinkPreHref: string = `/${SonLink.sonLinkClass}/`;

        public href: string;
        public id: number;
        public name: string;
        public capacity: number;
        public source: SonNode;
        public target: SonNode;
        public sourceHref: string;
        public targetHref: string;
        public weight: number;
        public src_port_label: string; //tslint:disable-line
        public dst_port_label: string; //tslint:disable-line
        public stats: {
            load?: number;
            latency?: number;
            error?: number;
            bitrate?: number;
        };
        public active: boolean;
        public multiservice: boolean;
        public bw: number;
        public vlink: boolean;
        public fromTo: string;
        public statsToPlot: ISonLinkStatsToPlot;
        public visited: boolean = false;

        constructor(l: Models.ISonLink) {
            this.href = SonLink.sonLinkPreHref + l.id;
            this.id = l.id;
            this.name = l.name;
            this.capacity = l.capacity * 1000;
            this.source = l.source;
            this.target = l.target;
            this.sourceHref = l.sourceHref;
            this.targetHref = l.targetHref;
            this.weight = l.weight;
            this.src_port_label = l.src_port_label;
            this.dst_port_label = l.dst_port_label;
            this.stats = {
                load: l.stats.load !== undefined ? Math.round(l.stats.load * 1000) / 1000 : undefined,
                bitrate: l.stats.bitrate !== undefined ? Math.round(l.stats.bitrate / 10000) / 100 : 0,
            };
            this.statsToPlot = {
                load: [0],
                bitrate: [0]
            };
            this.active = l.active;
            this.multiservice = l.multiservice;
            this.bw = l.bw * 1000;
            this.vlink = l.vlink;
            this.fromTo = this.targetHref === undefined ? `SON-${this.source.id}-${this.target.id}` : `SONDevice-${this.source.id}-${this.target.id}`;
        }

        public class(): TopologyElementType {
            return TopologyElementType.SonLink;
        }

        public getDisplayImage(): string {
            return 'images/iconmonstr-link-6-240.png';
        }
    }
}
